import { useCustomerManagementStore } from "_hooks";
import { Form, Input, Select } from "antd";
import React, { useCallback, useEffect, useMemo } from "react";
import {
  StyledFormPhoneNumber,
  StyledFormPhoneNumberGroup,
} from "./index.styled";
import { getCountriesOptions, getSelectFilterOption } from "_helpers";
import { getCountryFromCountryCodeOrName } from "country-codes-flags-phone-codes";
import { ContainerButton } from "_lib";
import PropTypes from "prop-types";

const FormPhoneNumber = (props) => {
  const { onSuccessCallback } = props;
  const {
    customerManagement: { countries, phoneVerificationStatus },
    fetchCountries,
    phoneVerification,
    clearPhoneVerificationStatus,
  } = useCustomerManagementStore();

  const [form] = Form.useForm();

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  const handlePhoneVerificationSuccess = useCallback(() => {
    clearPhoneVerificationStatus();
    const values = form.getFieldsValue();
    if (onSuccessCallback && typeof onSuccessCallback === "function") {
      onSuccessCallback(values);
    }
  }, [clearPhoneVerificationStatus, form, onSuccessCallback]);

  useEffect(() => {
    if (phoneVerificationStatus === "success") {
      handlePhoneVerificationSuccess();
    }
  }, [handlePhoneVerificationSuccess, phoneVerificationStatus]);

  const onFieldChange = useCallback(
    (changedValues) => {
      let currentChangedValue = changedValues[0] || {};
      if (currentChangedValue?.name?.[0] === "country") {
        let country = (countries || []).filter(
          ({ name }) => name === currentChangedValue.value
        );
        const code = country[0]?.code;
        country = getCountryFromCountryCodeOrName(code);
        form.setFieldValue("prefix", country?.dialCode);
      }
    },
    [countries, form]
  );

  const checkPhoneNumber = useCallback(
    (_, value) => {
      const numberRegex = new RegExp("^[0-9]+$");
      const leadingZeroRegex = new RegExp(/\b[1-9]\d{0,}/g);
      if (value) {
        if (!numberRegex.test(value)) {
          return Promise.reject(new Error("Please enter a valid phone number"));
        }
        if (value?.length > 15) {
          form.setFieldValue("phoneNumber", value.slice(0, -1));
        }

        if (!leadingZeroRegex.test(value)) {
          return Promise.reject(
            new Error("Phone number cannot start with 0 (Zero)")
          );
        }
        if (value?.length < 4) {
          return Promise.reject(
            new Error("Minimum phone number length is 4!!")
          );
        }
        return Promise.resolve();
      }
      return Promise.reject(new Error("Please enter a valid phone number"));
    },
    [form]
  );

  const onFinish = useCallback(
    (values) => {
      values.phoneNumber = `${values.prefix}${values.phoneNumber}`;
      delete values.prefix;

      phoneVerification(values);
    },
    [phoneVerification]
  );

  const countriesOptions = useMemo(() => {
    return getCountriesOptions(countries);
  }, [countries]);

  const prefixSelector = (
    <Form.Item name="prefix" noStyle>
      <Input className="prefix" readOnly placeholder="Code" />
    </Form.Item>
  );

  const loading = phoneVerificationStatus === "loading";

  return (
    <StyledFormPhoneNumber
      form={form}
      onFieldsChange={onFieldChange}
      onFinish={onFinish}
    >
      <StyledFormPhoneNumberGroup>
        <Form.Item
          name="country"
          rules={[
            {
              required: true,
              message: "Please input your Country!",
            },
          ]}
        >
          <Select
            showSearch
            placeholder="Select your Country"
            options={countriesOptions}
            size="large"
            filterOption={getSelectFilterOption}
          />
        </Form.Item>
        <Form.Item
          name="phoneNumber"
          rules={[
            {
              required: true,
              validator: checkPhoneNumber,
              min: 4,
            },
          ]}
        >
          <Input
            addonBefore={prefixSelector}
            placeholder="Phone number"
            size="large"
            inputMode="tel"
          />
        </Form.Item>
      </StyledFormPhoneNumberGroup>

      <ContainerButton
        size="large"
        title="Verify Now"
        type="primary"
        fullWidth
        loading={loading}
        htmlType="submit"
      />
    </StyledFormPhoneNumber>
  );
};

FormPhoneNumber.propTypes = {
  onSuccessCallback: PropTypes.func.isRequired,
};

export default FormPhoneNumber;
